


// Vue
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapActions } from 'vuex';
import { Mixins } from 'vue-mixin-decorator';

// Third Party
import dayjs, { UnitType } from 'dayjs';
import 'dayjs/locale/es';

// Models
import { Client } from '@/manager/client/client.model';

// Constants
import Assets from '@/constants/assets.ts';
import Constants from '@/constants/constants';

// Components
import Btn, { BtnColor, BtnWidth } from '@/shared/components/btn.component.vue';
import CheckIcon, { CheckIconColor } from '@/shared/components/CheckIcon.component.vue';
import Field from '@/shared/components/field.component.vue';
import FieldSet from '@/shared/components/field-set.component.vue';
import { IconEnum } from '@/shared/components/icon.component.vue';
import Input from '@/shared/components/input.component.vue';
import SearchAutocomplete from '@/shared/components/SearchAutocomplete.component.vue';
import TextArea from '@/shared/components/text-area.component.vue';
import TopModal from '@/shared/components/TopModal.component.vue';
import FlatSelectorText, {
    FlatSelectorTextPredefinedStyle,
    FlatSelectorTextSize,
} from '@/shared/components/FlatSelectorText.component.vue';

// Mixins
import SearchMixin from '@/shared/mixins/search.mixin.ts';
import ErrorMixin from '@/shared/mixins/error.mixin.ts';

// Services
import UtilsService from '@/core/services/utils.service';
import { ConvocationService, TypeOfProduct } from '@/manager/convocation/convocation.service';


interface MixinInterface extends ErrorMixin, SearchMixin {}

@Component({
    components: {
        Btn,
        CheckIcon,
        Field,
        FieldSet,
        Input,
        SearchAutocomplete,
        TextArea,
        TopModal,
        FlatSelectorText,
    },
    computed: {
        ...mapGetters({
            getProfile: 'getProfile',
            clientList: 'getClientList',
            getProcedencyList: 'procedencyList',
        }),
    },
})
export default class AddParticipantModal extends Mixins<MixinInterface> (ErrorMixin, SearchMixin) {
    @Prop({ default: true }) private opened!: boolean;
    @Prop() private convocation!: any;
    @Prop({ default: false }) private teamParticipant!: boolean;
    @Prop({ default: false }) private addToWaitingList!: boolean;


    private assets = Assets;
    private btnColor = BtnColor;
    private btnWidth = BtnWidth;
    private iconEnum = IconEnum;
    private checkIconColor = CheckIconColor;
    private utilsService = UtilsService;
    private getProfile!: any;
    private newClient: Client = new Client();
    private lastNoteTyped: string = '';
    private selectedClient: any | null = null;
    private clientList!: Client[];
    private getProcedencyList !: any;
    private nifMask = '';
    private flatSelectorTextPredefinedStyle = FlatSelectorTextPredefinedStyle;
    private flatSelectorTextSize = FlatSelectorTextSize;
    private procedencyOptions: any = [];
    private procedencySelected: any = {};
    private procedencyOption: any  = {
        value: '0',
        label: '',
    };
    public error = {
        user: {
            firstName: '',
            lastName: '',
            email: '',
            profile: {
                phone: '',
            },
        },
    };
    protected searchTimeout: any = null


    @Watch('newClient.user.profile.nif')
    private watchNif(newVal: string, oldVal: string) {
        this.nifMask = UtilsService.setNifMask(newVal, this.nifMask);
    }

    @Watch('procedencyOption')
    private watchProcedency(newVal: any, oldVal: any) {
        (this.newClient.procedency as any) = newVal.value;
    }

    @Watch('newClient.user.profile.phone')
    private watchPhone(newVal: any, oldVal: any) {
        if (!newVal) {
            (this.newClient.user as any).profile.phone = '+';
        } else if (newVal.charAt(0) != '+'){
            newVal = '+' + newVal;
            (this.newClient.user as any).profile.phone = newVal;
        } else {
            (this.newClient.user as any).profile.phone = newVal;
        }  
    }

    private created() {
        this.remainingTime = 0;
        this.fetchProcedency();
    }


    get isCourse() {
        return ConvocationService.convocationIsProductType(this.convocation, TypeOfProduct.Course);
    }

    get isConfe() {
        return ConvocationService.convocationIsProductType(this.convocation, TypeOfProduct.Conference);
    }

    get profileAvatar() {
        let avatar = this.assets.defaultAvatar;
        if (
            this.getProfile.profile &&
            this.getProfile.profile.avatar !== null
        ) {
            avatar = this.getProfile.profile.avatar;
        }
        return avatar;
    }

    get hasSelectedClient() {
        return (this.selectedClient !== null);
    }

    @Watch("searchString")
    public watchsearchString(newVal: any, oldVal: any) {
        if (this.searchTimeout != null) {
        clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
        this.searchCallback();
        }, 500);
    }

    public searchCallback() {
        if (this.searchString === '') {
            this.selectedClient = null;
        }
        this.fetchClientList();
    }

    private isLetter(str: string) {
        return str.length === 1 && str.match(/[a-zA-Z]/i);
    }

    private isNumber(str: string) {
        return str.length === 1 && str.match(/[0-9]/i);
    }

    private fetchClientList() {
        const payload = {
            limit: 20,
            search: this.searchString,
        };
        this.$store.dispatch( 'fetchClientList', payload);
    }

    private closeModal() {
        if ( this.opened ) {
            this.$emit('close-modal-add-participant', false);
        }
    }

    private checkMandatoryFields() {
        let result = true;
        if (this.newClient.user) {
            if (this.newClient.user.firstName === '') {
                this.error.user.firstName = 'Campo vacío';
                result = false;
            }
            if (this.newClient.user.lastName === '') {
                this.error.user.lastName = 'Campo vacío';
                result = false;
            }
            if (this.newClient.user.email === '') {
                this.error.user.email = 'Campo vacío';
                result = false;
            }
            if (
                Object.keys(this.newClient.user.profile).length === 0 &&
                this.newClient.user.profile.constructor === Object &&
                this.newClient.user.profile.phone !== '' && this.isConfe
            ) {
                this.error.user.profile.phone = 'Campo vacío';
                result = false;
            }
        }
        return result;
    }

    private setParticipantFormError(err: any) {
        const serverError = err.response.data;
        this.error = { ...this.error, ...serverError };
        if (err.response.status === 406) {
            setTimeout(() => {
                this.$emit('error-new-participant', err.response.data.detail);
            }, 300);
        } else if (err.response.status === 500) {
            this.$emit('error-new-participant', Constants.internalServerErrorMessage);
        } else if (err.response.status === 400) {
            this.$emit('error-new-participant', err.response.data);
        }
    }

    private addParticipant() {
        if (this.checkMandatoryFields()) {
            if (!this.newClient.tlp ) {
                delete this.newClient.tlp
            }
            const client = this.newClient;
            if ((client.user as any).profile.phone) {
                (client.user as any).profile.phone = (client.user as any).profile.phone.replaceAll(/\s/g,'');
            }
            const note = { note: this.lastNoteTyped };
            const team = this.teamParticipant;

            if ( client.user !== undefined) {
                delete client.user.profile.avatar;
            }

            this.$store.dispatch(
                'newParticipant',
                {
                    convocationId: this.convocation.id,
                    client,
                    note,
                    team,
                    addToWaitingList: this.addToWaitingList,
                },
            ).then(
                (res) => {
                    this.closeModal();
                    this.lastNoteTyped = '';
                    this.clearClient();
                    setTimeout(() => {
                        this.$emit('new-participant', true);
                    }, 300);
                },
            ).catch((err) => {
                this.setParticipantFormError(err);
            });
        }
    }

    private selectClient(option: any) {
        this.selectedClient = option;
        (this.newClient as any).user.profile.nif = '';
        setTimeout(() => {
            this.fillWithClientData();
        }, 100);
    }

    private clearClient() {
        this.selectedClient = null;
        this.newClient = new Client();
        this.procedencySelectedMethod();
    }

    private fillWithClientData() {
        if (this.selectedClient !== null) {
            const client = this.$store.getters.getClient(this.selectedClient.id);
            this.newClient = client;
            this.procedencySelectedMethod();
        }
    }

    private parseProcedencyToFlatSelectorTextOption(procedencyList: any) {
        this.procedencyOptions = procedencyList.map(
            (procedency: any) => {
                const option = {
                    value: procedency.id,
                    label: procedency.name,
                };
                return option;
            },
        );
    }

    private fetchProcedency() {
        this.$store.dispatch('fetchProcedency').then(
            (res) => {
                this.parseProcedencyToFlatSelectorTextOption(this.getProcedencyList);
            },
        );
    }

    private procedencySelectedMethod() {
        const procedencyId = this.newClient.procedency;
        let result = { value: 0, label: '' };
        if ( procedencyId ) {
            result = this.procedencyOptions.find( (option: any) => {
                    return procedencyId && option.value === procedencyId;
                },
            );
        }
        this.procedencySelected = result;
    }
}
